import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import Contact from '../components/Contact';
import Icon from '../components/Icon';
import Eyebrow from '../components/page/Eyebrow';
import StepRow from '../components/page/StepRow';
import { SITE } from '../components/Seo';
import { trackEvent } from '../utils/analytics';
import { PHONE, EMAIL, TELEGRAM, VIBER, POSTAL_ADDRESS } from '../content/contacts';

// Для schema.org потрібні «голі» значення без розмітки посилання.
const EMAIL_ADDRESS = EMAIL.href.replace('mailto:', '');
const PHONE_E164 = PHONE.href.replace('tel:', '');

/**
 * Прямі канали. Телефон — окремою великою плиткою: хто прийшов на сторінку
 * контактів із телефона, найчастіше хоче просто подзвонити, і номер має
 * натискатися одним дотиком, а не шукатися в переліку. Раніше канали стояли
 * списком із порядковими номерами 01–05, які нічого не означали, а номер
 * телефону в Telegram і Viber рвався посеред цифр.
 */
const MESSENGERS = [
  { key: 'telegram', icon: TELEGRAM.icon, title: 'Telegram', href: TELEGRAM.href },
  { key: 'viber', icon: VIBER.icon, title: 'Viber', href: VIBER.href }
];

/**
 * Що відбувається після звернення. Найчастіше людина не пише саме тому, що не
 * знає, у що це її втягне, — тож простіше сказати наперед.
 */
const afterYouWrite = [
  { title: 'Ми відповідаємо', text: 'Уточнюємо, що вже зроблено й що є на руках: приміщення, персонал, документи, попередні відмови.' },
  { title: 'Кажемо, що потрібно', text: 'Називаємо перелік робіт і послідовність. Якщо частину можна зробити без нас — теж кажемо.' },
  { title: 'Погоджуємо умови', text: 'Строк і вартість відомі до початку роботи, а не з’являються в процесі.' }
];

const Channels = () => (
  <section aria-labelledby="channels-title">
    <Eyebrow>Напряму</Eyebrow>
    <h2 id="channels-title" className="sr-only">Прямі контакти</h2>

    <div className="grid grid-cols-2 gap-3">
      <a
        href={PHONE.href}
        onClick={() => trackEvent('contact_channel', 'contact-page-phone')}
        className="group col-span-2 relative overflow-hidden flex items-center gap-4 rounded-2xl bg-gradient-to-r from-teal-700 to-sky-700 p-5 text-white shadow-md hover:shadow-lg transition-shadow duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
      >
        <span className="absolute -top-10 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" aria-hidden="true"></span>
        <span className="relative flex-shrink-0 w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="fa-phone" className="text-lg" />
        </span>
        <span className="relative min-w-0">
          <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-white/90">Подзвонити</span>
          <span className="block text-xl sm:text-2xl font-extrabold font-display whitespace-nowrap">{PHONE.label}</span>
        </span>
        <Icon
          name="fa-arrow-right"
          className="relative ml-auto flex-shrink-0 opacity-80 transition-transform duration-200 group-hover:translate-x-1"
        />
      </a>

      {MESSENGERS.map((channel) => (
        <a
          key={channel.key}
          href={channel.href}
          onClick={() => trackEvent('contact_channel', `contact-page-${channel.key}`)}
          className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
        >
          <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
            <Icon name={channel.icon} className="text-lg" />
          </span>
          <span className="min-w-0">
            <span className="block font-semibold text-slate-900">{channel.title}</span>
            <span className="block text-sm text-slate-600">Написати</span>
          </span>
        </a>
      ))}

      <a
        href={EMAIL.href}
        onClick={() => trackEvent('contact_channel', 'contact-page-email')}
        className="col-span-2 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
      >
        <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
          <Icon name="fa-envelope" className="text-lg" />
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-slate-900 break-words">{EMAIL.label}</span>
          <span className="block text-sm text-slate-600">Для документів і довгих листів</span>
        </span>
      </a>
    </div>

    <p className="mt-4 flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
      <Icon name="fa-building" className="mt-0.5 flex-shrink-0 text-teal-700" />
      Ми у Львові й працюємо дистанційно по всій Україні: реєстрація, ліцензування, ЕСОЗ і торговельна марка не потребують вашої присутності.
    </p>
  </section>
);

const ContactPage = () => {
  const schema = {
    '@type': 'ContactPage',
    inLanguage: 'uk-UA',
    mainEntity: {
      '@type': 'LegalService',
      name: 'Visia Medical Group',
      url: SITE,
      email: EMAIL_ADDRESS,
      telephone: PHONE_E164,
      address: POSTAL_ADDRESS,
      areaServed: { '@type': 'Country', name: 'Україна' },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: EMAIL_ADDRESS,
        telephone: PHONE_E164,
        availableLanguage: ['uk', 'en']
      }
    }
  };

  return (
    <PageShell
      title="Контакти й консультація юриста"
      description="Зв’яжіться з Visia Medical Group: пошта, телефон і форма запиту. Консультації щодо ліцензування медичної практики, ЕСОЗ і документації клінік по всій Україні."
      path="/kontakty"
      banner="/images/page-consult-e51e9060.jpg"
      schema={schema}
      crumbs={[{ name: 'Контакти' }]}
      heading="Зв’язатися з нами"
      lead="Розкажіть, на якому ви етапі — і ми скажемо, що знадобиться саме вам, у які строки та за яких умов."
    >
      <div className="page-container py-10 lg:py-16">
        {/*
          Форма — перше, що бачить людина під банером: заради неї сюди й
          приходять. Раніше до неї треба було прогорнути вступ, картку
          контактів і три кроки — на телефоні це три з половиною екрани.

          Від lg форма ліворуч, канали праворуч — дві колонки близької висоти;
          кроки «що буде далі» — окремим рядом на всю ширину під ними. Нижче
          lg порядок у DOM і є порядком показу: спершу канали (номер одним
          дотиком), потім форма, потім кроки.
        */}
        <div className="grid gap-8 lg:gap-12 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1">
            <Channels />
          </div>

          <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1">
            <Contact bare />
          </div>
        </div>

        <StepRow
          id="next-steps"
          caption="Без сюрпризів"
          title="Що буде після звернення"
          steps={afterYouWrite}
        />

        <div className="mt-14 lg:mt-20 rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex items-start gap-4 flex-1">
            <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
              <Icon name="fa-comment-dots" className="text-lg" />
            </span>
            <div>
              <h2 className="text-lg font-bold font-display text-slate-900">Ще не знаєте, з чого почати?</h2>
              <p className="mt-1 text-slate-600 leading-relaxed">
                Здебільшого відповідь уже є в частих запитаннях або в розборі типових ситуацій — і писати не доведеться.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:flex-shrink-0">
            <Link
              to="/pytannya"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-800 hover:border-teal-600 hover:text-teal-800 transition-colors duration-200"
            >
              Часті запитання
            </Link>
            <Link
              to="/z-chogo-pochaty"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-800 hover:border-teal-600 hover:text-teal-800 transition-colors duration-200"
            >
              З чого почати
              <Icon name="fa-arrow-right" className="text-sm" />
            </Link>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ContactPage;
